import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/CTASection";
import {
  IMAGES, PARTNERS, WHY, SERVICES, INDUSTRIES, MORE_INDUSTRIES, COMMITMENTS, FAQS, STATS,
  BOOKING_URL, SERVICE_AREAS,
} from "../data/site";
import { track } from "../lib/analytics";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Managed IT Services in Atlanta | Zentryx Systems — IT Support & Cybersecurity"
        description="Zentryx Systems is Atlanta's managed IT services and cybersecurity partner for small and midsize businesses across every industry — responsive support, proactive protection, and clear guidance. Book a free IT assessment."
        path="/"
      />

      {/* HERO */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-mist" />
        <div className="absolute top-0 right-0 -z-10 h-[480px] w-[480px] rounded-full bg-teal/5 blur-3xl" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-teal bg-teal/10 px-4 py-1.5 rounded-full mb-6">
              Atlanta Managed IT &amp; Cybersecurity
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-[1.05]">
              Managed IT for Atlanta businesses — <span className="text-navy">secure, reliable, and moving.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Zentryx Systems is Atlanta's local managed IT and cybersecurity partner. We support small and midsize businesses of every kind across the Atlanta metro — with responsive support, proactive protection, and practical guidance for every industry we serve.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => track("book_intro_call_click", { location: "hero" })}
                data-testid="hero-booking-button"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
                Book an Intro Call <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/contact" data-testid="hero-book-button"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-slate-50 text-navy font-semibold border border-slate-200 transition-colors">
                Request a Free IT Assessment
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={IMAGES.hero} alt="Zentryx Systems IT consultants working with an Atlanta business team"
                className="rounded-3xl shadow-2xl shadow-slate-300/50 w-full h-[420px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-6 py-4 hidden sm:block">
                <p className="font-heading text-xl font-extrabold text-navy">Atlanta-based</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Local, responsive support</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section data-testid="trusted-by" className="py-12 border-y border-slate-100 bg-white overflow-hidden">
        <p className="text-center text-xs uppercase tracking-widest font-semibold text-slate-400 mb-8">
          Built on trusted technology partners
        </p>
        <div className="relative max-w-6xl mx-auto px-5 overflow-hidden">
          <div className="flex gap-16 animate-marquee w-max items-center">
            {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, i) => (
              <span key={`${p}-${i}`} className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-500 whitespace-nowrap">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ZENTRYX */}
      <section data-testid="why-section" className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Why Zentryx</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              A real IT partner — not just another vendor.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300">
                  <div className="h-12 w-12 rounded-2xl bg-navy/5 flex items-center justify-center mb-6">
                    <w.icon className="h-6 w-6 text-navy" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 sm:py-32 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Services</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              Everything you need from an IT partner.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              From day-to-day help desk support to cybersecurity, Microsoft 365, cloud, and strategic planning — Zentryx covers the technology your Atlanta business depends on.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link to="/services" className="block h-full rounded-3xl bg-white border border-slate-200 p-8 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300">
                  <div className="h-12 w-12 rounded-2xl bg-teal/10 flex items-center justify-center mb-6">
                    <s.icon className="h-6 w-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/services" data-testid="services-link"
              className="inline-flex items-center gap-2 font-semibold text-navy hover:gap-3 transition-all">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="industries-section" className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Industries</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              Serving businesses across Atlanta.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              We support small and midsize businesses of every kind across the Atlanta metro. Two industries are areas of deep, specialized expertise — CPA/accounting and architecture/engineering firms — where we know your deadlines, data sensitivity, and the tools you rely on.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={i * 0.08}>
                <Link to={ind.detailPath} className="group block rounded-3xl overflow-hidden border border-slate-200 bg-white h-full hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300">
                  <div className="h-44 overflow-hidden">
                    <img src={ind.img} alt={`IT support for ${ind.title} in Atlanta`}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2.5 mb-2">
                      <ind.icon className="h-5 w-5 text-navy" />
                      <h3 className="font-heading text-lg font-bold text-charcoal">{ind.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{ind.body}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:gap-2.5 transition-all">
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {/* ALSO SERVE — industries we support */}
          <Reveal>
            <p className="mt-14 mb-6 text-sm uppercase tracking-widest font-semibold text-slate-400">
              We also support Atlanta businesses in
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {MORE_INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.title} delay={(i % 4) * 0.06}>
                <div data-testid={`more-industry-${i}`}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
                  <span className="h-9 w-9 rounded-xl bg-navy/5 flex items-center justify-center flex-shrink-0">
                    <ind.icon className="h-4.5 w-4.5 text-navy" />
                  </span>
                  <p className="font-heading font-semibold text-charcoal text-sm leading-tight">{ind.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MONEY PAGES / WHO WE'RE BEST FOR */}
      <section data-testid="focus-pages-section" className="py-24 sm:py-32 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-4">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Managed IT for Atlanta businesses that need more than generic IT support</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              Solutions built around your business.
            </h2>
          </Reveal>
          <Reveal>
            <p className="text-slate-600 leading-relaxed max-w-3xl mb-10">
              Whatever your industry, Zentryx delivers secure, responsive IT support and a partner who understands your work. Explore our deep-dive resources for the specialized industries we know best.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { label: "Managed IT for CPA Firms", to: "/industries/cpa-accounting-firms-atlanta" },
              { label: "Cybersecurity for CPA Firms", to: "/industries/cybersecurity-for-cpa-firms-atlanta" },
              { label: "Tax-Season IT Support", to: "/industries/tax-season-it-support-for-accounting-firms" },
              { label: "Managed IT for Architecture Firms", to: "/industries/architecture-engineering-firms-atlanta" },
              { label: "Managed IT for Engineering Firms", to: "/industries/managed-it-for-engineering-firms-atlanta" },
              { label: "IT for AutoCAD, Revit & Bluebeam", to: "/industries/it-support-autocad-revit-bluebeam" },
            ].map((m, i) => (
              <Reveal key={m.to} delay={(i % 3) * 0.06}>
                <Link to={m.to} data-testid={`focus-link-${i}`}
                  className="group flex items-center justify-between gap-3 rounded-2xl bg-white border border-slate-200 px-6 py-5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-200/60 transition-all">
                  <span className="font-heading font-semibold text-charcoal">{m.label}</span>
                  <ArrowRight className="h-4 w-4 text-navy group-hover:translate-x-1 transition-transform" />
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 text-sm text-slate-500 leading-relaxed">
              Proudly serving businesses across {SERVICE_AREAS.join(", ")}.
            </p>
          </Reveal>
        </div>
      </section>

      {/* REPRESENTATIVE EXAMPLE */}
      <section data-testid="case-study-section" className="py-24 sm:py-32 px-5 sm:px-8 bg-navy text-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.caseStudy} alt="24/7 IT monitoring operations for Atlanta businesses"
              className="rounded-3xl w-full h-[400px] object-cover shadow-2xl" />
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-sm uppercase tracking-widest font-semibold text-white/70 mb-4">How we help</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              What a Zentryx partnership looks like.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              When a growing Atlanta accounting firm came to us with recurring outages and security gaps, we standardized their systems, strengthened their defenses, moved them to a well-managed Microsoft 365 setup, and put tested backups in place — so busy season ran smoothly and their team could focus on clients. It's the same practical, security-first approach we bring to every engagement.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[["Reliable", "Systems that stay up"], ["Protected", "Layered security"], ["Responsive", "Support they can reach"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-heading text-2xl font-extrabold text-white">{v}</p>
                  <p className="text-xs text-slate-400 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="py-16 px-5 sm:px-8 bg-mist border-y border-slate-100">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <p className="font-heading text-2xl sm:text-3xl font-extrabold text-navy">{s.value}</p>
              <p className="text-sm text-slate-500 mt-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMMITMENTS */}
      <section data-testid="commitments-section" className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Our commitment</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              What you can count on.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-8">
                  <div className="h-12 w-12 rounded-2xl bg-navy/5 flex items-center justify-center mb-6">
                    <c.icon className="h-6 w-6 text-navy" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 sm:py-32 px-5 sm:px-8 bg-mist">
        <div className="max-w-3xl mx-auto">
          <Reveal className="mb-12 text-center">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">FAQ</p>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-charcoal">
              Questions, answered.
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white px-6">
                  <AccordionTrigger className="font-heading font-semibold text-charcoal text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
